/* eslint-disable default-case,no-prototype-builtins,complexity,fp/no-loops,max-depth */
/**
 * Module validate provides an implementation of JSON Typedef validation.
 *
 * The most important function in this module is {@link validate}, which returns
 * an array of {@link ValidationError}. You can configure {@link validate} by
 * passing it a {@link ValidationConfig}.
 *
 * @packageDocumentation
 */

// import isRFC3339 from './rfc3339';
import type { Schema, SchemaFormType } from './schema.js';
import {
  isDiscriminatorForm,
  isElementsForm,
  isEnumForm,
  isPropertiesForm,
  isRefForm,
  isTypeForm,
  isValuesForm,
} from './schema.js';

/**
 * ValidationConfig represents options you can pass to {@link validate}.
 */
export interface ValidationConfig {
  /**
   * maxDepth is the maximum number of `ref`s to recursively follow before
   * {@link validate} throws {@link MaxDepthExceededError}.
   *
   * If maxDepth is zero, then no maximum depth will be enforced.
   * {@link validate} will recursively follow `ref`s indefinitely, potentially
   * causing a stack overflow.
   *
   * By default, maxDepth is zero.
   */
  maxDepth?: number;

  /**
   * maxErrors is the maximum number of errors to return from {@link validate}.
   *
   * If maxErrors is positive, {@link validate} may return fewer errors than
   * maxErrors, but it will never return more than maxErrors.
   *
   * If maxErrors is zero, {@link validate} will return all validation errors.
   *
   * By default, maxErrors is zero.
   */
  maxErrors?: number;

  features?: string[];
}

/**
 * MaxDepthExceededError is the error returned if
 * {@link ValidationConfig.maxDepth} is exceeded during {@link validate}.
 */
class MaxDepthExceededError extends Error {}

class MaxErrorsReachedError extends Error {}

/**
 * ValidationError represents a JSON Typedef validation error.
 *
 * In terms of the formal JSON Typedef specification, ValidationError
 * corresponds to a JSON Typedef error "indicator". ValidationError is *not* a
 * subclass of Error. It is just a plain old TypeScript interface.
 *
 * Both elements of ValidationError are meant to be used as the path segments of
 * an [RFC6901 JSON Pointer](https://tools.ietf.org/html/rfc6901). This package
 * does not provide an implementation of JSON Pointers for you, and you may
 * choose to not use JSON Pointers at all.
 */
export interface ValidationError {
  /**
   * instancePath is the path to a part of the instance, or "input", that was
   * rejected.
   */
  instancePath: string[];

  /**
   * schemaPath is the path to the part of the schema that rejected the input.
   */
  schemaPath: string[];
  message: string;
}

/**
 * validate performs JSON Typedef validation of an instance (or "input") against
 * a JSON Typedef schema, returning a standardized set of errors.
 *
 * This function may throw {@link MaxDepthExceededError} if you have configured
 * a {@link ValidationConfig.maxDepth}. If you do not configure such a maxDepth,
 * then this function may cause a stack overflow. That's because of
 * circularly-defined schemas like this one:
 *
 * ```json
 * {
 *   "ref": "loop",
 *   "definitions": {
 *     "loop": { "ref": "loop" }
 *   }
 * }
 * ```
 *
 * If your schema is not circularly defined like this, then there is no risk for
 * validate to overflow the stack.
 *
 * If you are only interested in a certain number of error messages, consider
 * using {@link ValidationConfig.maxErrors} to get better performance. For
 * instance, if all you care about is whether the input is OK or if it has
 * errors, you may want to set maxErrors to 1.
 *
 * @param schema The schema to validate data against
 * @param instance The "input" to validate
 * @param config Validation options. Optional.
 */

export function validate(
  schema: Schema,
  instance: unknown,
  config?: ValidationConfig
): ValidationError[] {
  const formats = schema.metadata?.formats;
  const validateFormat = createFormatValidator(formats);

  const state = {
    errors: [],
    instanceTokens: [],
    schemaTokens: [[]],
    root: schema,
    config: {
      maxDepth: config?.maxDepth ?? 0,
      maxErrors: config?.maxErrors ?? 0,
      features: config?.features,
    },
  };

  try {
    validateWithState(state, schema, instance, { format: validateFormat });
  } catch (err) {
    if (err instanceof MaxErrorsReachedError) {
      // MaxErrorsReachedError is just a dummy error to abort further
      // validation. The contents of state.errors are what we need to return.
    } else {
      // This is a genuine error. Let's re-throw it.
      throw err;
    }
  }

  return state.errors;
}

interface ValidationState {
  errors: ValidationError[];
  instanceTokens: string[];
  schemaTokens: string[][];
  root: Schema;
  config: ValidationConfig;
}

type NonStandardValidations = {
  format: (schema: Schema, instance: unknown) => string | undefined;
};

function validateWithState(
  state: ValidationState,
  schema: Schema,
  instance: unknown,
  nonStandardValidations: NonStandardValidations,
  parentTag?: string
) {
  if (schema.nullable && instance === null) {
    return;
  }

  if (
    state.config.features &&
    schema.metadata?.features &&
    !schema.metadata.features.some(f => state.config.features?.includes(f)) &&
    instance !== null &&
    !isEmptyObject(instance)
  ) {
    pushSchemaToken(state, 'metadata');
    pushSchemaToken(state, 'features');
    pushError(
      state,
      schema.metadata.features.length > 1
        ? `Features [${schema.metadata.features.join(', ')}] are not supported`
        : `Feature [${schema.metadata.features[0]}] is not supported`
    );
    popSchemaToken(state);
    popSchemaToken(state);
  }

  if (isRefForm(schema)) {
    if (state.schemaTokens.length === state.config.maxDepth) {
      throw new MaxDepthExceededError();
    }

    // The ref form is the only case where we push a new array onto
    // schemaTokens; we maintain a separate stack for each reference.
    state.schemaTokens.push(['definitions', schema.ref]);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    validateWithState(state, state.root.definitions![schema.ref], instance, nonStandardValidations);
    state.schemaTokens.pop();
  } else if (isTypeForm(schema)) {
    pushSchemaToken(state, 'type');

    switch (schema.type) {
      case 'boolean':
        if (typeof instance !== 'boolean') {
          pushError(state, 'Expected a boolean');
        }
        break;
      case 'float32':
      case 'float64':
        if (typeof instance !== 'number') {
          pushError(state, 'Expected a number');
        }
        break;
      case 'int8':
        validateInt(state, instance, schema, -128, 127);
        break;
      case 'uint8':
        validateInt(state, instance, schema, 0, 255);
        break;
      case 'int16':
        validateInt(state, instance, schema, -32768, 32767);
        break;
      case 'uint16':
        validateInt(state, instance, schema, 0, 65535);
        break;
      case 'int32':
        validateInt(state, instance, schema, -2147483648, 2147483647);
        break;
      case 'uint32':
        validateInt(state, instance, schema, 0, 4294967295);
        break;
      case 'string':
        if (typeof instance !== 'string') {
          pushError(state, 'Expected a string');
        }
        if (
          schema.metadata?.validation?.minLength &&
          (instance as string).length < schema.metadata.validation.minLength
        ) {
          pushSchemaToken(state, ['metadata', 'validation', 'minLength']);
          pushError(state, `Expected at least ${schema.metadata.validation.minLength} characters`);
          popSchemaToken(state, 3);
        }

        if (
          schema.metadata?.validation?.maxLength &&
          (instance as string).length > schema.metadata.validation.maxLength
        ) {
          pushSchemaToken(state, ['metadata', 'validation', 'maxLength']);
          pushError(state, `Expected at most ${schema.metadata.validation.minLength} characters`);
          popSchemaToken(state, 3);
        }
        break;
      case 'timestamp':
        if (typeof instance !== 'string') {
          pushError(state, 'Expected a timestamp');
          // } else if (!isRFC3339(instance)) {
          //   pushError(state);
        }
        break;
    }
    const formatError = nonStandardValidations.format(schema, instance);
    if (formatError) {
      pushError(state, formatError);
    }

    popSchemaToken(state);
  } else if (isEnumForm(schema)) {
    pushSchemaToken(state, 'enum');

    if (typeof instance !== 'string' || !schema.enum.includes(instance)) {
      pushError(state, `Expected one of: ${schema.enum.join(', ')}`);
    }

    popSchemaToken(state);
  } else if (isElementsForm(schema)) {
    pushSchemaToken(state, 'elements');

    if (Array.isArray(instance)) {
      for (const [index, subInstance] of instance.entries()) {
        pushInstanceToken(state, index.toString());
        validateWithState(state, schema.elements, subInstance, nonStandardValidations);
        popInstanceToken(state);
      }

      if (schema.metadata?.pattern) {
        const pattern = schema.metadata.pattern;
        const validationResult = validatePattern(pattern.value, instance);
        if (!validationResult.valid) {
          pushSchemaToken(state, ['metadata', 'pattern']);
          pushError(state, validationResult.message);
          popSchemaToken(state, 2);
        }
      }
      if (
        schema.metadata?.validation?.maxSize &&
        instance.length > schema.metadata.validation.maxSize
      ) {
        pushSchemaToken(state, ['metadata', 'validation', 'maxSize']);
        pushError(state, `Expected at most ${schema.metadata.validation.maxSize} items`);
        popSchemaToken(state, 3);
      }

      if (
        schema.metadata?.validation?.minSize &&
        instance.length < schema.metadata.validation.minSize
      ) {
        pushSchemaToken(state, ['metadata', 'validation', 'minSize']);
        pushError(state, `Expected at least ${schema.metadata.validation.minSize} items`);
        popSchemaToken(state, 3);
      }

      if (schema.metadata?.validation?.uniqueTags) {
        const repeating = instance
          .map(i => (i as any)?.type as string)
          .filter((item, index, arr) => arr.indexOf(item) !== index);
        if (repeating.length) {
          pushSchemaToken(state, ['metadata', 'validation', 'uniqueTags']);
          pushError(state, `Expected unique types, but [${repeating.join(', ')}] are repeated`);
          popSchemaToken(state, 3);
        }
      }
    } else {
      pushError(state, 'Expected an array');
    }

    popSchemaToken(state);
  } else if (isPropertiesForm(schema)) {
    // JSON has six basic types of data (null, boolean, number, string,
    // array, object). Of their standard JS countparts, three have a
    // `typeof` of "object": null, array, and object.
    //
    // This check attempts to check if something is "really" an object.
    if (typeof instance === 'object' && instance !== null && !Array.isArray(instance)) {
      if (schema.properties !== undefined) {
        pushSchemaToken(state, 'properties');
        for (const [name, subSchema] of Object.entries(schema.properties)) {
          pushSchemaToken(state, name);
          if (instance.hasOwnProperty(name)) {
            pushInstanceToken(state, name);
            validateWithState(state, subSchema, (instance as any)[name], nonStandardValidations);
            popInstanceToken(state);
          } else {
            pushError(state, `Missing property: ${name}`);
          }
          popSchemaToken(state);
        }
        popSchemaToken(state);
      }

      if (schema.optionalProperties !== undefined) {
        pushSchemaToken(state, 'optionalProperties');
        for (const [name, subSchema] of Object.entries(schema.optionalProperties)) {
          pushSchemaToken(state, name);
          if (instance.hasOwnProperty(name)) {
            const value = (instance as any)[name];
            if (value !== undefined) {
              pushInstanceToken(state, name);
              validateWithState(state, subSchema, value, nonStandardValidations);
              popInstanceToken(state);
            }
          }
          popSchemaToken(state);
        }
        popSchemaToken(state);
      }

      if (schema.additionalProperties !== true) {
        for (const name of Object.keys(instance)) {
          const inRequired = schema.properties && name in schema.properties;
          const inOptional = schema.optionalProperties && name in schema.optionalProperties;
          const value = (instance as any)[name];

          if (!inRequired && !inOptional && name !== parentTag && value !== undefined) {
            pushInstanceToken(state, name);
            pushError(state, 'Unexpected property');
            popInstanceToken(state);
          }
        }
      }
      if (schema.metadata?.oneOf) {
        const oneOfProperties = schema.metadata?.oneOf.properties;
        const countOfInitializedOneOfProperties = oneOfProperties.filter(p => {
          return (
            Object.hasOwn(instance, p) &&
            (instance[p as keyof typeof instance] !== null ||
              instance[p as keyof typeof instance] !== undefined)
          );
        }).length;
        if (countOfInitializedOneOfProperties > 1) {
          pushSchemaToken(state, ['metadata', 'oneOf']);
          pushError(
            state,
            `Only one of the properties [${oneOfProperties.join(', ')}] can be initialized`
          );
          popSchemaToken(state, 2);
        }
        if (schema.metadata.oneOf.strict && countOfInitializedOneOfProperties < 1) {
          pushSchemaToken(state, ['metadata', 'oneOf']);
          pushError(
            state,
            `One property in oneOf group [${oneOfProperties.join(',')}] must be initialized`
          );
          popSchemaToken(state, 2);
        }
      }
    } else {
      if (schema.properties !== undefined) {
        pushSchemaToken(state, 'properties');
      } else {
        pushSchemaToken(state, 'optionalProperties');
      }

      pushError(state, 'Expected an object');
      popSchemaToken(state);
    }
  } else if (isValuesForm(schema)) {
    pushSchemaToken(state, 'values');

    // See comment in properties form on why this is the test we use for
    // checking for objects.
    if (typeof instance === 'object' && instance !== null && !Array.isArray(instance)) {
      for (const [name, subInstance] of Object.entries(instance)) {
        pushInstanceToken(state, name);
        validateWithState(state, schema.values, subInstance, nonStandardValidations);
        popInstanceToken(state);
      }
    } else {
      pushError(state, 'Expected an object in map form');
    }

    popSchemaToken(state);
  } else if (isDiscriminatorForm(schema)) {
    // See comment in properties form on why this is the test we use for
    // checking for objects.
    if (typeof instance === 'object' && instance !== null && !Array.isArray(instance)) {
      if (instance.hasOwnProperty(schema.discriminator)) {
        const tag = (instance as any)[schema.discriminator];

        if (typeof tag === 'string') {
          if (tag in schema.mapping) {
            pushSchemaToken(state, 'mapping');
            pushSchemaToken(state, tag);
            validateWithState(
              state,
              schema.mapping[tag],
              instance,
              nonStandardValidations,
              schema.discriminator
            );
            popSchemaToken(state);
            popSchemaToken(state);
          } else {
            pushSchemaToken(state, 'mapping');
            pushInstanceToken(state, schema.discriminator);
            pushError(state, `Allowed values: ${Object.keys(schema.mapping).join(', ')}`);
            popInstanceToken(state);
            popSchemaToken(state);
          }
        } else {
          pushSchemaToken(state, 'discriminator');
          pushInstanceToken(state, schema.discriminator);
          pushError(state, 'Expected a string');
          popInstanceToken(state);
          popSchemaToken(state);
        }
      } else {
        pushSchemaToken(state, 'discriminator');
        pushError(state, `Missing tag: ${schema.discriminator}`);
        popSchemaToken(state);
      }
    } else {
      pushSchemaToken(state, 'discriminator');
      pushError(state, 'Expected an object');
      popSchemaToken(state);
    }
  }
}

function validateInt(
  state: ValidationState,
  instance: unknown,
  schema: SchemaFormType,
  min: number,
  max: number
) {
  if (
    typeof instance !== 'number' ||
    !Number.isInteger(instance) ||
    instance < min ||
    instance > max
  ) {
    pushError(state, `Expected an integer between ${min} and ${max}`);
  }

  if (schema.metadata?.validation?.min && Number(instance) < schema.metadata.validation.min) {
    pushSchemaToken(state, ['metadata', 'validation', 'min']);
    pushError(state, `Expected min value of ${schema.metadata.validation.min}`);
    popSchemaToken(state, 3);
  }

  if (schema.metadata?.validation?.max && Number(instance) > schema.metadata.validation.max) {
    pushSchemaToken(state, ['metadata', 'validation', 'max']);
    pushError(state, `Expected max value of ${schema.metadata.validation.max}`);
    popSchemaToken(state, 3);
  }
}

function pushInstanceToken(state: ValidationState, token: string) {
  state.instanceTokens.push(token);
}

function popInstanceToken(state: ValidationState) {
  state.instanceTokens.pop();
}

function pushSchemaToken(state: ValidationState, token: string | string[]) {
  state.schemaTokens[state.schemaTokens.length - 1].push(
    ...(Array.isArray(token) ? token : [token])
  );
}

function popSchemaToken(state: ValidationState, count = 1) {
  for (let i = 0; i < count; i++) {
    state.schemaTokens[state.schemaTokens.length - 1].pop();
  }
}

function pushError(state: ValidationState, message: string) {
  state.errors.push({
    instancePath: [...state.instanceTokens],
    schemaPath: [...state.schemaTokens[state.schemaTokens.length - 1]],
    message,
  });

  if (state.errors.length === state.config.maxErrors) {
    throw new MaxErrorsReachedError();
  }
}

function createFormatValidator(formats: NonNullable<Schema['metadata']>['formats']) {
  if (!formats) {
    return () => undefined;
  }
  return (schema: Schema, instance: unknown): string | undefined => {
    if (!schema.metadata?.format || !('type' in schema)) {
      return undefined;
    }

    if (schema.type !== 'string') {
      throw new Error(
        `Invalid schema: Format validation can only be applied to string type form (got ${schema.type})`
      );
    }
    if (!instance) {
      // If the instance is empty, we don't need to validate the format, other validators exist for this
      return undefined;
    }
    const format = formats?.[schema.metadata.format];
    if (format?.validation.type === 'regex') {
      const regex = new RegExp(format.validation.regex);
      if (regex.test(instance as string)) {
        return undefined;
      }
      return `Incorrect format (expected: /${format.validation.regex})/ ${format.description})`;
    }
    if (format?.validation.type === 'web_url') {
      try {
        // eslint-disable-next-line no-new
        new URL(instance as string);
      } catch (e: unknown) {
        return 'Incorrect format (expected: web_url): ' + (e as Error).message;
      }
      const url = new URL(instance as string);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return 'Incorrect format (expected: web_url): URL must start with http:// or https://';
      }
    }
    return undefined;
  };
}

const validatePattern = (
  pattern: string,
  instance: unknown[]
): { valid: true } | { valid: false; message: string } => {
  const nodeArrangement = instance.map(o => (o as any)?.type ?? 'UNDEFINED').join('');

  const isValid = !!nodeArrangement.match(`^${pattern.replaceAll(' ', '')}$`);
  if (isValid) {
    return { valid: true };
  }
  return {
    valid: false,
    message: `Incorrect node order [${instance
      .map(o => (o as any)?.type ?? 'UNDEFINED')
      .join(' ')}] does not match required pattern [${pattern}]`,
  };
};

const isEmptyObject = (obj: unknown) => obj && typeof obj === 'object' && !Object.keys(obj).length;
